/**
 * Zentrale Client-Konfiguration für Web + native Apps (Capacitor, ADR-042).
 *
 * - **Web-Build:** `API_BASE = ''` → relative Pfade, Session per HttpOnly-Cookie.
 *   Verhalten identisch zu vorher (kein Token im Client).
 * - **Nativer Build:** `API_BASE = https://nest.rehntiere.de` (Build-Zeit-Var
 *   `VITE_API_BASE`); Auth per **Bearer-Token** (REST) bzw. **First-Message** (WS).
 *   Der Token wird nur im nativen Kontext gespeichert.
 */

/** Läuft die App in einer nativen Capacitor-Hülle (iOS/Android)? */
export const isNative: boolean =
  typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() === true

/** Basis-URL der API. Leer im Web (same-origin), gesetzt im nativen Build. */
export const API_BASE: string = (import.meta.env.VITE_API_BASE ?? '').replace(/\/+$/, '')

/** Absolute API-/WS-URL aus einem relativen Pfad (`/api/…`, `/ws`) bilden. */
export function apiUrl(path: string): string {
  if (/^(https?|wss?):\/\//i.test(path)) return path
  return API_BASE + path
}

/**
 * URL für ein vom Server geliefertes Asset (Avatare, Immich-/Tandoor-Bilder).
 * Nur relative `/…`-Pfade werden im nativen Build mit `API_BASE` präfixt; im Web
 * (leeres `API_BASE`) und bei absoluten URLs bleibt der Wert unverändert.
 */
export function assetUrl(src: string | null | undefined): string | undefined {
  if (!src) return undefined
  if (!API_BASE || !src.startsWith('/')) return src
  return API_BASE + src
}

// ── Token-Speicher (nur nativer Kontext; Web nutzt weiterhin das HttpOnly-Cookie) ──
// ADR-068 (Native Phase 1b): Der Bearer-Token liegt nativ in der **Keychain/Keystore**
// (capacitor-secure-storage-plugin), nicht mehr im `localStorage`. `memToken` bleibt die
// synchrone Quelle (apiFetch ist synchron); `initToken()` befüllt es beim App-Start aus
// dem Secure Store (mit Einmal-Migration eines Alt-Tokens aus dem localStorage). Im Web
// ist alles ein No-op.
const TOKEN_KEY = 'fn_token'
let memToken: string | null = null

function localGet(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}
function localRemove(): void {
  try {
    window.localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

// Dynamischer Import → das native Plugin landet in einem eigenen Lazy-Chunk und
// belastet den Web-Build nicht.
//
// ⚠️ WICHTIG (ADR-072): Den Plugin-**Proxy** NIEMALS `await`en oder aus einer
// async-Funktion `return`en! Capacitor-Plugin-Proxies fangen JEDEN Property-Zugriff
// ab — auch `.then`. Beim `await`/`return` prüft die Promise-Maschinerie `.then`,
// ruft damit nativ `SecureStoragePlugin.then()` auf („not implemented on android"),
// und die Assimilation bleibt hängen → `initToken()` löst nie auf → in `bootstrap()`
// wird `createRoot` nie erreicht → **weißer Bildschirm**. Nur die **Methoden**
// (get/set/remove) awaiten; den Proxy stets synchron aus dem Modul-Namespace holen.
async function loadSecureStorage(): Promise<typeof import('capacitor-secure-storage-plugin')> {
  return import('capacitor-secure-storage-plugin')
}

/**
 * Token beim App-Start in `memToken` laden (nur nativ). Liest aus dem Secure Store;
 * findet er dort nichts, migriert er einmalig einen Alt-Token aus dem localStorage.
 * Fällt bei fehlendem Plugin auf den localStorage zurück. Im Web sofort fertig.
 */
export async function initToken(): Promise<void> {
  if (!isNative) return
  try {
    const { SecureStoragePlugin } = await loadSecureStorage() // Namespace awaiten (safe), Proxy NICHT
    try {
      const { value } = await SecureStoragePlugin.get({ key: TOKEN_KEY })
      memToken = value || null
      return
    } catch {
      // Kein Eintrag im Secure Store → evtl. Alt-Token aus localStorage übernehmen.
      const legacy = localGet()
      if (legacy) {
        memToken = legacy
        try {
          await SecureStoragePlugin.set({ key: TOKEN_KEY, value: legacy })
          localRemove()
        } catch {
          /* Migration best-effort */
        }
      }
    }
  } catch {
    // Plugin nicht verfügbar → Fallback localStorage.
    memToken = localGet()
  }
}

async function persistToken(token: string | null): Promise<void> {
  try {
    const { SecureStoragePlugin } = await loadSecureStorage()
    if (token) await SecureStoragePlugin.set({ key: TOKEN_KEY, value: token })
    else await SecureStoragePlugin.remove({ key: TOKEN_KEY }).catch(() => {})
    return
  } catch {
    /* Fallback localStorage */
  }
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token)
    else window.localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* nur In-Memory */
  }
}

export function getToken(): string | null {
  if (!isNative) return null
  if (memToken !== null) return memToken
  memToken = localGet() // Fallback, falls initToken() noch nicht durchlief
  return memToken
}

export function setToken(token: string | null): void {
  if (!isNative) return
  memToken = token
  void persistToken(token)
}

/**
 * `fetch` gegen die API: relative Pfade werden mit `API_BASE` absolut gemacht und
 * im nativen Kontext der Bearer-Header gesetzt. Im Web unverändert (same-origin,
 * Cookie). Content-Type o. Ä. bleiben dem Aufrufer überlassen (FormData-Uploads!).
 */
export function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = getToken()
  const headers = new Headers(init.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  return fetch(apiUrl(path), { ...init, headers })
}
